import { Stack } from '@mui/material'
import { CheckIcon } from '@phosphor-icons/react'
import { Image, InfoText, Modal, tokens, User } from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { useTranslation } from 'react-i18next'
import { type AppraiserSelectionSectionCopy, AppraiserSelectionSection } from './AppraiserSelectionSection'

type HandoverModalProps = {
    isHandoverOpen: boolean
    onClose: () => void
    onConfirm: () => void
    isPersisting?: boolean
    hasAppraiserInObservers?: boolean
    hasAppraiserInObserversOrParticipants?: boolean
    appraiserUsers: User[]
    selectedAppraiserId?: number
    setSelectedAppraiserId: (selectedId?: number) => void
    isAppraiserUsersPending?: boolean
    copy?: Partial<HandoverModalCopy>
}

export type HandoverModalCopy = {
    title: string
    close: string
    primaryButton: string
    secondaryButton: string
    imageAlt: string
    makeAnnotationsVisible: string
    requestNewDraft: string
    infoText: string
    appraiserSelection: Partial<AppraiserSelectionSectionCopy>
}

export function HandoverModal({
    isHandoverOpen,
    onClose,
    onConfirm,
    isPersisting,
    hasAppraiserInObservers,
    hasAppraiserInObserversOrParticipants,
    appraiserUsers,
    selectedAppraiserId,
    setSelectedAppraiserId,
    isAppraiserUsersPending,
    copy: copyOverrides,
}: HandoverModalProps) {
    const { t: tCommonActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tConfirmation } = useTranslation('terminology', { keyPrefix: 'common.confirmation' })
    const { t: tPlausibility } = useTranslation('terminology', { keyPrefix: 'plausibility' })
    const { t: tAppraiserSelectionSection } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.appraiserSelectionSection',
    })
    const { t: tHandoverModal } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valuation.valuationPlausibility.handoverModal',
    })
    const copy: HandoverModalCopy = {
        title: copyOverrides?.title ?? tPlausibility('shareAnnotations'),
        close: copyOverrides?.close ?? tCommonActions('close'),
        primaryButton: copyOverrides?.primaryButton ?? tHandoverModal('actions.confirm'),
        secondaryButton: copyOverrides?.secondaryButton ?? tConfirmation('cancel'),
        imageAlt: copyOverrides?.imageAlt ?? tHandoverModal('imageAlt'),
        makeAnnotationsVisible: copyOverrides?.makeAnnotationsVisible ?? tPlausibility('makeAnnotationsVisible'),
        requestNewDraft: copyOverrides?.requestNewDraft ?? tHandoverModal('items.requestNewDraft'),
        infoText: copyOverrides?.infoText ?? tHandoverModal('infoText'),
        appraiserSelection: {
            title: tAppraiserSelectionSection('title'),
            description: tAppraiserSelectionSection('description'),
            loading: tAppraiserSelectionSection('loading'),
            empty: tAppraiserSelectionSection('emptyState'),
            selectLabel: tAppraiserSelectionSection('fieldLabel'),
            ...copyOverrides?.appraiserSelection,
        },
    }
    const hasResponsibleAppraiser = hasAppraiserInObservers ?? hasAppraiserInObserversOrParticipants ?? false
    const hasAppraiserOptions = appraiserUsers.length > 0
    const hasSelectedAppraiser = selectedAppraiserId !== undefined

    return (
        <Modal
            title={copy.title}
            isOpen={isHandoverOpen}
            onClose={onClose}
            showCloseIcon
            closeIconTooltip={copy.close}
            footerProps={{
                primaryButton: {
                    text: copy.primaryButton,
                    onClick: onConfirm,
                    disabled:
                        isPersisting ||
                        (!hasResponsibleAppraiser && isAppraiserUsersPending) ||
                        (!hasResponsibleAppraiser && hasAppraiserOptions && !hasSelectedAppraiser),
                },
                secondaryButton: { text: copy.secondaryButton, onClick: onClose },
            }}
        >
            <Stack direction="column" gap={tokens.SizingSpacing300} width="100%">
                <Stack
                    alignItems="center"
                    justifyContent="center"
                    paddingY={tokens.SizingSpacing150}
                    bgcolor={tokens.ColorGraphSequentialTwoLightest}
                    width="100%"
                >
                    <Image src="/handoverOrdinary.svg" alt={copy.imageAlt} width={135} height={135} />
                </Stack>

                <Stack direction="column" gap={tokens.SizingSpacing150}>
                    <Stack direction="row" gap={tokens.SizingSpacing075}>
                        <CheckIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} style={{ flexShrink: 0 }} />
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {copy.makeAnnotationsVisible}
                        </Typography>
                    </Stack>
                    <Stack direction="row" gap={tokens.SizingSpacing075}>
                        <CheckIcon size={tokens.SizingIconS} weight="bold" color={tokens.ColorIconDefault} style={{ flexShrink: 0 }} />
                        <Typography variant="textS" color={tokens.ColorTextDefault}>
                            {copy.requestNewDraft}
                        </Typography>
                    </Stack>
                    <InfoText size="small" variant="information" text={copy.infoText} />

                    {!hasResponsibleAppraiser && (
                        <AppraiserSelectionSection
                            appraiserUsers={appraiserUsers}
                            selectedAppraiserId={selectedAppraiserId}
                            setSelectedAppraiserId={setSelectedAppraiserId}
                            isAppraiserUsersPending={isAppraiserUsersPending}
                            copy={copy.appraiserSelection}
                        />
                    )}
                </Stack>
            </Stack>
        </Modal>
    )
}
